import React, { useEffect, useState, Dispatch } from 'react';
import { SimulationProperties } from '@Components/SimulationProperties';
import { FeatureItem, SimulationItem } from '@Utils/types';
import {
	SensorHistoryDialog,
	SensorHistoryDialogProps,
} from '@Components/SensorHistoryDialog';
import {
	addChartToWorkspace,
	generateSimulationTableId,
	getWorkspaces,
	selectWorkspaces,
	SimulationPrimarySeries,
	Workspace,
	infoWaterProSimulationsSelector,
	SharedStoreState,
} from '@innovyze/stylovyze';
import { SimulationAttributes } from '@Types/simulation.types';
import { Resolutions } from '@innovyze/summaryze';
import { AddToWorkspaceDialog } from '@Components/AddToWorkspaceDialog';
import { useDispatch, useSelector } from 'react-redux';

interface SimulationsTabProps {
	items?: SimulationItem[];
	setActiveTab: Dispatch<React.SetStateAction<number | undefined>>;
	simulationsTabIndex?: number;
}

const featureItemToSimulationItem = (item: FeatureItem): SimulationItem => ({
	...item,
	id: item.id,
	objectId: item.objectId ?? '',
	display_name: item.display_name ?? '',
	type: item.type ?? '',
	startDateTime: item.startDateTime ?? '',
	endDateTime: item.endDateTime ?? '',
	simulations: item.simulations ?? [],
	resultId: item.resultId ?? 0,
	analysisGroupId: item.analysisGroupId ?? 0,
	dbid: item.dbid ?? '',
	modellingId: item.modellingId ?? '',
});

export const SimulationsTab = ({
	items = [],
	setActiveTab,
	simulationsTabIndex,
}: SimulationsTabProps): JSX.Element => {
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [modalTitle, setModalTitle] = useState('');
	const dispatch = useDispatch();
	const [primarySeries, setPrimarySeries] = useState<
		SensorHistoryDialogProps['primarySeries']
	>();

	useEffect(() => {
		if (!modalOpen || !simulationsTabIndex) {
			return;
		}
		setActiveTab(simulationsTabIndex);
	}, [modalOpen]);

	const handleViewSimulationChart = ({
		id,
		name,
		attribute,
	}: SimulationAttributes) => {
		const simulation = items.find(({ simulations: attributes }) => {
			const simulationIds = attributes.map(({ id }) => id);

			return simulationIds.includes(id);
		});
		if (!simulation) {
			return;
		}

		const tableId = generateSimulationTableId(simulation.type);
		const simulationObject: SimulationPrimarySeries & {
			modellingId?: string;
		} = {
			analysisGroupId: simulation.analysisGroupId.toString(),
			attribute,
			databaseId: simulation.dbid,
			objectId: simulation.objectId,
			resultId: `${simulation.resultId}`,
			tableId,
			modellingId: simulation.modellingId,
		};
		setModalTitle(name);
		setPrimarySeries({
			id: simulation.objectId,
			func: 'Close',
			resolution: Resolutions['Raw'],
			displayOptions: {},
			alias: name,
			unit: '',
			simulation: simulationObject,
		});
		setModalOpen(true);
	};
	const [workspaceModalOpen, setWorkspaceModalOpen] = useState<boolean>(
		false,
	);

	useEffect(() => {
		dispatch(getWorkspaces({}));
	}, []);

	const workspaces = useSelector(selectWorkspaces);
	const closeWorkspaceModal = () => setWorkspaceModalOpen(false);
	const { data: simulationLabels } = useSelector(state =>
		infoWaterProSimulationsSelector(
			(state as unknown) as SharedStoreState,
			primarySeries?.simulation?.databaseId ?? '',
			primarySeries?.simulation?.analysisGroupId ?? '',
		),
	);

	const mapObjectIdToModellingId = (
		primarySeries: SensorHistoryDialogProps['primarySeries'] | undefined,
	) => {
		if (!primarySeries) {
			return;
		}
		const { simulation } = primarySeries;
		if (!simulation) {
			return primarySeries;
		}
		const { modellingId } = simulation;
		if (modellingId) {
			// In case modellingId is provided, use it to fetch simulation data, otherwise use objectId
			return {
				...primarySeries,
				simulation: {
					...simulation,
					objectId: modellingId,
				},
			};
		}
		return primarySeries;
	};

	const addToWorkspace = (workspace: Workspace) => {
		if (!primarySeries) {
			return;
		}
		closeWorkspaceModal();
		const { label } =
			simulationLabels.find(
				({ id }) => id === primarySeries.simulation?.resultId,
			) ?? {};

		const { simulation } = primarySeries;
		const { objectId, attribute } = simulation ?? {};
		const { type } =
			items.find(
				({ resultId }) =>
					resultId.toString() === primarySeries.simulation?.resultId,
			) ?? {};

		const assetType =
			type && type?.charAt(0).toUpperCase() + type?.slice(1);

		const chartName = `${label} - ${assetType} - ${objectId} - ${attribute}`;
		let processedPrimarySeries: SensorHistoryDialogProps['primarySeries'] = {
			...primarySeries,
			alias: chartName,
		};
		processedPrimarySeries = mapObjectIdToModellingId(
			processedPrimarySeries,
		);
		dispatch(
			addChartToWorkspace({
				workspaceId: workspace.id,
				primarySeries: processedPrimarySeries,
				name: chartName,
			}),
		);
	};

	return (
		<>
			<SensorHistoryDialog
				title={modalTitle}
				modalOpen={modalOpen}
				closeModal={() => {
					setModalOpen(false);
					setPrimarySeries(undefined);
				}}
				primarySeries={mapObjectIdToModellingId(primarySeries)}
				setWorkspaceModalOpen={setWorkspaceModalOpen}
			/>
			<AddToWorkspaceDialog
				open={workspaceModalOpen}
				workspaces={workspaces}
				onCancel={closeWorkspaceModal}
				onConfirm={addToWorkspace}
			/>
			<div data-cy="tab-item-list">
				{items.map(item => (
					<SimulationProperties
						key={item.id}
						item={featureItemToSimulationItem(item)}
						onViewChartClick={handleViewSimulationChart}
					/>
				))}
			</div>
		</>
	);
};
